import React, { useCallback, useMemo, useState } from 'react';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import dayjs from 'dayjs';
import classes from './CustomCalendarPicker.module.scss';
import colors from '../../../scss/colors';
import { calendarTypes } from '../../../utils/constants';

export default function CustomCalendarPicker({
  userDate = dayjs(),
  setUserDate,
  calendarType,
  backgroundColor = colors.PAGE_BACKGROUND,
  disabledBeforeIncluded
}) {
  const [dateToShow, setDateToShow] = useState(dayjs());

  const currentDay = useMemo(() => dayjs().toDate(), []);

  const firstDayOfTheMonth = useMemo(() => dateToShow.clone().startOf('month'), [dateToShow]);

  const firstDayOfFirstWeekOfMonth = useMemo(() => {
    if (firstDayOfTheMonth.day() === 0) {
      return dayjs(firstDayOfTheMonth.add(-1, 'day')).startOf('week');
    }
    return dayjs(firstDayOfTheMonth).startOf('week');
  }, [firstDayOfTheMonth]);

  const generateFirstDayOfEachWeek = useCallback((day) => {
    const dates = [day];
    for (let i = 1; i < 6; i++) {
      const dateFD = day.clone().add(i, 'week');
      dates.push(dateFD);
    }
    return dates;
  }, []);

  const generateWeek = useCallback((day) => {
    const dates = [];
    for (let i = 1; i < 8; i++) {
      const dateW = day.clone().add(i, 'day').toDate();
      dates.push(dateW);
    }
    return dates;
  }, []);

  const generateWeeksOfTheMonth = useMemo(() => {
    const firstDayOfEachWeek = generateFirstDayOfEachWeek(firstDayOfFirstWeekOfMonth);
    return firstDayOfEachWeek.map((dateFDEW) => generateWeek(dateFDEW));
  }, [generateFirstDayOfEachWeek, firstDayOfFirstWeekOfMonth, generateWeek]);

  const getClassForVariant = (day) => {
    if (dateToShow.clone().toDate().getMonth() !== day.getMonth()) {
      return classes.nextMonth;
    }
    if (dayjs(currentDay).isSame(day, 'date')) {
      return classes.today;
    }

    if (dayjs(userDate).isSame(day) && calendarType === calendarTypes.DAY) {
      return classes.selected;
    }

    return classes.default;
  };

  const getClassWeekVariant = (week) => {
    if (calendarType === calendarTypes.WEEK) {
      return week.some((item) => dayjs(item.toString()).isSame(userDate, 'date'))
        ? `${classes.calendarWeekWrapper} ${classes.selected}`
        : classes.calendarWeekWrapper;
    }

    return classes.calendarDayWrapper;
  };

  const isDisabled = (day) => (disabledBeforeIncluded ? dayjs(day).isBefore(disabledBeforeIncluded) : false);

  return (
    <div className={classes.mainWrapper} style={{ backgroundColor }}>
      <div className={classes.calendarHeaderWrapper}>
        <p>{dateToShow.clone().format('MMM YYYY')}</p>
        <div className={classes.actionButtons}>
          <ArrowBackIosNewIcon
            fontSize="small"
            onClick={() => setDateToShow((dateBack) => dateBack.subtract(1, 'month'))}
            sx={{ color: '#D1D3D4' }}
          />
          <ArrowForwardIosIcon
            fontSize="small"
            onClick={() => setDateToShow((dateForward) => dateForward.add(1, 'month'))}
            sx={{ color: '#D1D3D4' }}
          />
        </div>
      </div>
      <div className={classes.weekDaysWrapper}>
        {generateWeeksOfTheMonth[0].map((day, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div className={classes.weekDayCell} key={`week-day-${index}`}>
            {dayjs(day).format('ddd')}
          </div>
        ))}
      </div>
      {generateWeeksOfTheMonth.map((week) => (
        <div key={`week-${week.toString()}`} className={getClassWeekVariant(week)}>
          {week.map((day) => (
            <button
              key={`day-${day.toString()}`}
              onClick={() => {
                if (!isDisabled(day)) setUserDate(day);
              }}
              className={`${classes.calendarDayCell} ${getClassForVariant(day)} ${
                isDisabled(day) ? classes.disabled : ''
              }`}
            >
              {day.getDate()}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}
